import Tiptap from '@components/Tiptap';
import useAuth from '@hooks/use-auth';
import {
  MenuItemStyleOptions,
  MenuItemStyleValues,
  MenuItemType,
  SubKeyboardType,
  SubKeyboardTypeLabels,
} from '@interfaces/menu-item-type';
import type { TelegramGroup } from '@interfaces/telegram-group';
import type { TelegramMenuItem } from '@interfaces/telegram-menu-item';
import { useForm } from '@pages/Management/hooks/use-editable-form';
import { ACTIONS } from '@pages/Management/MenuManagement/action';
import { isResponse, isSuccessfulDataResponse } from '@services/fetcher';
import fetchConversations from '@utils/api/fetchConversations';
import { Alert, Button, Form, Input, Modal, Select, Switch } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

interface MenuItemWithDefaultTranslation extends Record<string, unknown> {
  id: number;
  frontend_label: string;
  has_subkeyboard: boolean;
  subkeyboard_type: TelegramMenuItem['subkeyboard_type'];
  type: 'button' | 'command';
  style?: TelegramMenuItem['style'];
  command: string | null;
  label: string;
  answer: string;
  service_group?: number | null;
}

interface OwnProps {
  open: boolean;
  onCreate: (menuItem: Omit<TelegramMenuItem, 'translations'>) => void;
  onCancel: () => void;
}

const MenuItemAddForm: FC<OwnProps> = ({ open, onCreate, onCancel }) => {
  const auth = useAuth();
  const {
    form,
    isLoadingOrSubmitting,
    submitForm,
    error: formError,
    resetFetcher,
  } = useForm<Omit<TelegramMenuItem, 'translations'>, MenuItemWithDefaultTranslation>(
    'menu-item-add-form',
    true,
    ({ response }) => {
      if (!response || isResponse(response)) {
        return;
      }

      onCreate(response);
    },
  );
  const itemType = Form.useWatch<MenuItemWithDefaultTranslation['type']>('type', form);
  const itemHasSubKeyboard = Form.useWatch<MenuItemWithDefaultTranslation['has_subkeyboard']>('has_subkeyboard', form);
  const [conversations, setConversations] = useState<TelegramGroup[]>();

  useEffect(() => {
    if (itemType === MenuItemType.Command) {
      form.setFieldValue('style', null);
    }
  }, [form, itemType]);

  useEffect(() => {
    async function fetchGroups() {
      const response = await fetchConversations(auth, 'filter[is_authorized]=true', null, null);
      if (!isSuccessfulDataResponse(response)) {
        return;
      }

      setConversations(response.data);
    }

    // noinspection JSIgnoredPromiseFromCall
    fetchGroups();
  }, [auth]);

  const onTriggerCancel = useCallback(() => {
    form.resetFields();
    resetFetcher();
    onCancel();
  }, [form, resetFetcher, onCancel]);

  return (
    <Modal
      open={open}
      title="Add menu item"
      onCancel={onTriggerCancel}
      footer={[
        <Button key="cancel" disabled={isLoadingOrSubmitting} onClick={onTriggerCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoadingOrSubmitting} onClick={form.submit}>
          Add
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={() => submitForm(ACTIONS.AddMenuItem, 'POST')}
        onKeyUp={(e) => {
          if (e.key !== 'Enter') {
            return;
          }
          const target = e.target as HTMLElement | null;
          if (target?.closest('.tiptap')) {
            return; // let RTE handle Enter/new lines
          }

          form.submit();
        }}
        disabled={isLoadingOrSubmitting}
      >
        {formError && <Alert message={formError} style={{ margin: '24px 0' }} type="error" />}
        <Form.Item
          name="label"
          label="Label (English)"
          rules={[
            {
              required: true,
              message: 'Please enter a label.',
            },
            {
              max: 64,
              message: 'Label can not exceed 64 characters.',
            },
          ]}
        >
          <Input count={{ show: true, max: 64 }} />
        </Form.Item>
        <Form.Item
          name="type"
          label="Type"
          rules={[
            {
              required: true,
              message: 'Please select a type.',
            },
          ]}
          initialValue="button"
        >
          <Select
            options={Object.entries(MenuItemType).map(([key, value]) => ({
              label: key,
              value,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="style"
          label="Appearance"
          initialValue={null}
          extra="Telegram controls the exact colors to match each user's theme."
          rules={[
            {
              enum: MenuItemStyleValues,
              message: 'Please select a supported appearance.',
            },
          ]}
        >
          <Select options={MenuItemStyleOptions} disabled={itemType !== MenuItemType.Button} />
        </Form.Item>
        {itemType === 'command' && (
          <Form.Item
            name="command"
            label="Command"
            rules={[
              {
                required: itemType === 'command',
                message: 'Please enter a command.',
              },
              {
                max: 32,
                message: 'The command can not exceed 32 characters.',
              },
              {
                pattern: /^[a-z0-9_]+$/,
                message: 'The command can only contain lowercase English letters, digits and underscores.',
              },
            ]}
          >
            <Input count={{ show: true, max: 32 }} />
          </Form.Item>
        )}
        <Form.Item name="has_subkeyboard" label="Subkeyboard" valuePropName="checked">
          <Switch />
        </Form.Item>
        {itemHasSubKeyboard === true && (
          <Form.Item
            name="subkeyboard_type"
            label="SubKeyboard Type"
            rules={[{ required: itemHasSubKeyboard, message: 'Please select a sub-keyboard type.' }]}
            initialValue={itemHasSubKeyboard ? 'numbers' : null}
          >
            <Select
              options={Object.entries(SubKeyboardType).map(([key, value]) => ({
                label: SubKeyboardTypeLabels[SubKeyboardType[key as unknown as keyof typeof SubKeyboardType]],
                value,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item name="answer" label="Telegram message (English)">
          <Tiptap />
        </Form.Item>
        <Form.Item name="frontend_label" label="Web-client message (English)">
          <Input />
        </Form.Item>
        <Form.Item
          name="service_group_id"
          label="Service Request Archive"
          help="All service requests will be automatically forwarded to this conversation."
        >
          <Select
            showSearch
            optionFilterProp="label"
            allowClear
            loading={conversations === undefined}
            options={conversations?.map((conversation) => ({
              value: conversation.id,
              label: conversation.name,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MenuItemAddForm;
